import { useToast } from 'components/ToastManager';
import {
  useAuthorizePlatformEntityBasisTheorySessionMutation,
  useGetPaymentPlatformEntityQuery,
} from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, {
  Suspense,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { match } from 'ts-pattern';
import { FragmentEnv } from 'utils/env';
import { getResultOrError } from 'utils/errors';
import type { ITextElement } from '@basis-theory/react-elements';
import {
  BasisTheoryProvider,
  useBasisTheory,
} from '@basis-theory/react-elements';
import { useQuery } from '@tanstack/react-query';

import { ColorModeContext } from '@fragment/ui/src/components/ColorMode/ColorModeContext';

import type { SideBarStageName } from './OnboardingLayout';
import { AccountsView } from './AccountsView';
import { ApplicationStatusView } from './ApplicationStatusView';
import { BusinessCDDView } from './BusinessCDDView';
import { BusinessView } from './BusinessView';
import { IncorporationView } from './IncorporationView';
import { NoticeView } from './NoticeView';
import { OnboardingLayout } from './OnboardingLayout';
import { initialOnboardingState, onboardingReducer } from './onboardingReducer';
import { PeopleView } from './PeopleView';

const STAGE_ORDER: SideBarStageName[] = [
  'notice',
  'business',
  'incorporation',
  'businessCDD',
  'people',
  'accounts',
  'applicationStatus',
];

const getNextStage = (stage: SideBarStageName): SideBarStageName => {
  const idx = STAGE_ORDER.indexOf(stage);
  if (idx === STAGE_ORDER.length - 1) {
    return stage;
  }
  return STAGE_ORDER[idx + 1];
};

export const PlatformOnboardingView = () => {
  const workspaceId = useWorkspaceId();
  const { mode, systemIsDark } = useContextSafely(ColorModeContext);
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemIsDark);
  // get entity
  const { context: internalApiCtx } = useInternalApiContext();
  const [{ data: entityData, error: entityError }] =
    useGetPaymentPlatformEntityQuery({
      variables: { workspaceId },
      context: internalApiCtx,
    });

  const {
    result,
    isError: isEntityError,
    errorMessage,
  } = getResultOrError(entityData?.paymentPlatformEntity, entityError);

  const piiDataTokenId = useMemo(
    () => result?.platformEntity.piiDataTokenId,
    [result]
  );

  // authorize basis theory session
  const { bt } = useBasisTheory();
  const [, authorizeSession] =
    useAuthorizePlatformEntityBasisTheorySessionMutation();

  const { showToast } = useToast();
  const { data: sessionKey, error: sessionError } = useQuery({
    queryKey: ['bt-session', workspaceId],
    enabled: bt != null,
    retry: false,
    queryFn: async () => {
      const { sessionKey: key, nonce } = await bt!.sessions.create();

      const authResult = await authorizeSession(
        { workspaceId, nonce },
        internalApiCtx
      );

      if (authResult.error) {
        throw new Error(authResult.error.message);
      }

      const data = authResult.data?.authorizePlatformEntityBasisTheorySession;
      if (!data) {
        throw new Error('No response from server');
      }

      if (
        data.__typename === 'BadRequestError' ||
        data.__typename === 'InternalError'
      ) {
        throw new Error(data.message);
      }

      if (!data.success) {
        throw new Error('Session authorization failed');
      }

      return key;
    },
  });

  // fill in data from retrieved token
  const {
    data: token,
    error: tokenError,
    refetch: retrieveToken,
  } = useQuery({
    queryKey: ['token', piiDataTokenId],
    enabled: bt != null && sessionKey != null && piiDataTokenId != null,
    retry: false,
    queryFn: async () =>
      bt!.tokens.retrieve(piiDataTokenId!, {
        apiKey: sessionKey!,
      }),
  });

  const updateToken = useCallback(
    (id: string, ref: React.RefObject<ITextElement>) => {
      if (bt && piiDataTokenId && ref.current) {
        bt.tokens
          .update(piiDataTokenId, {
            data: {
              [id]: ref.current,
            },
          })
          .then((res) => {
            retrieveToken();
          })
          .catch((error) => {
            console.error('Error updating token', error.data);
          });
      }
    },
    [bt, piiDataTokenId, retrieveToken]
  );

  const [currentStage, setCurrentStage] = useState<SideBarStageName>('notice');
  const [completedStages, setCompletedStages] = useState<SideBarStageName[]>(
    []
  );
  const [onboardingState, dispatch] = useReducer(
    onboardingReducer,
    initialOnboardingState
  );

  const setOnboardingField = useCallback(
    (stateKey: string, value: string | string[] | boolean) => {
      dispatch({ type: 'SET_FIELD', stateKey, value });
    },
    []
  );

  const handleNextClick = useCallback(() => {
    setCompletedStages((prev) => [...prev, currentStage]);
    setCurrentStage(getNextStage(currentStage));
  }, [currentStage]);

  const renderStageContent = useMemo(() => {
    if (
      !bt ||
      !result?.platformEntity.piiDataTokenId ||
      !sessionKey ||
      !token
    ) {
      return <span>Loading...</span>;
    }
    return match(currentStage)
      .with('notice', () => <NoticeView />)
      .with('business', () => (
        <BusinessView
          token={token}
          updateToken={updateToken}
          isDarkMode={isDarkMode}
        />
      ))
      .with('incorporation', () => (
        <IncorporationView
          onboardingState={onboardingState}
          setOnboardingField={setOnboardingField}
          token={token}
          updateToken={updateToken}
          isDarkMode={isDarkMode}
        />
      ))
      .with('businessCDD', () => (
        <BusinessCDDView
          onboardingState={onboardingState}
          setOnboardingField={setOnboardingField}
          token={token}
          updateToken={updateToken}
          isDarkMode={isDarkMode}
        />
      ))
      .with('people', () => (
        <PeopleView
          onboardingState={onboardingState}
          setOnboardingField={setOnboardingField}
        />
      ))
      .with('accounts', () => (
        <AccountsView goToPlaid={() => {}} goToNextPage={handleNextClick} />
      ))
      .with('applicationStatus', () => (
        <ApplicationStatusView onboardingState={onboardingState} />
      ))
      .exhaustive();
  }, [
    currentStage,
    onboardingState,
    setOnboardingField,
    handleNextClick,
    bt,
    result?.platformEntity.piiDataTokenId,
    sessionKey,
    token,
    updateToken,
    isDarkMode,
  ]);

  if (sessionError || tokenError || isEntityError) {
    return <div>Failed to load onboarding data, please refresh the page.</div>;
  }

  return (
    <OnboardingLayout
      currentStage={currentStage}
      completedStages={completedStages}
      onClickStage={setCurrentStage}
      onNextButtonClick={handleNextClick}
      isNextDisabled={false}
      workspaceId={workspaceId}
    >
      {renderStageContent}
    </OnboardingLayout>
  );
};

export const PlatformOnboarding = () => {
  const { dashboardApiKey } = FragmentEnv.basisTheory!;
  const { bt } = useBasisTheory(dashboardApiKey);
  if (!dashboardApiKey) {
    return <div>Basis Theory is not configured for this environment.</div>;
  }
  return (
    <BasisTheoryProvider bt={bt}>
      <Suspense fallback={<div>Loading...</div>}>
        <PlatformOnboardingView />
      </Suspense>
    </BasisTheoryProvider>
  );
};
